import { buildCartLandingShopperKey } from "@/utils/cart/cartLandingShopper"
import { clearCartLandingViewModel } from "@/utils/cart/cartLandingViewModel"

export { buildCartLandingShopperKey }

const STORAGE_KEY = "kohler.cartLandingSnapshot.v1"
const VIEW_MODEL_STORAGE_KEY = "kohler.cartLandingViewModel.v1"
const SNAPSHOT_VERSION = 2
const MAX_AGE_MS = 24 * 60 * 60 * 1000
const MAX_SNAPSHOT_LENGTH = 512 * 1024
const IDLE_WRITE_TIMEOUT_MS = 1000

const CART_ATTRIBUTE_NAMES = new Set([
  "ComboSKU",
  "CustomerFacingBrand",
  "CustomerFacingSKU",
  "ProductBrandNameDisplay",
  "ProductBrandNameDisplay_t",
  "ProductDescriptionProduct",
  "ProductDescriptionProductShort",
  "ProductLocalCategory",
  "ProductResource",
  "ProductSection",
  "ProductServices",
  "ProductShape",
  "ProductSize",
  "RegionBrandName",
  "RegionLeadTime",
  "SKUColorFinishName",
  "SPProductBusinessUnit",
])

let pendingWrite = null
let writeGeneration = 0
let invalidatedSessionId = ""

const compactObject = entries =>
  Object.fromEntries(entries.filter(([, value]) => value !== undefined))

const cloneAllowedValue = value => {
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value
  }

  if (Array.isArray(value)) {
    return value
      .map(cloneAllowedValue)
      .filter(clonedValue => clonedValue !== undefined)
  }

  if (value && typeof value === "object") {
    return Object.entries(value).reduce((result, [key, nestedValue]) => {
      if (["__proto__", "constructor", "prototype"].includes(key)) {
        return result
      }
      const clonedValue = cloneAllowedValue(nestedValue)
      if (clonedValue !== undefined) result[key] = clonedValue
      return result
    }, {})
  }

  return undefined
}

const sanitizeMoney = money => {
  if (!money || typeof money !== "object") return undefined
  return compactObject([["centAmount", money.centAmount]])
}

const sanitizeAttributes = attributes => {
  if (!Array.isArray(attributes)) return []
  return attributes
    .filter(attribute => CART_ATTRIBUTE_NAMES.has(attribute?.name))
    .map(attribute =>
      compactObject([
        ["name", attribute.name],
        ["value", cloneAllowedValue(attribute.value)],
      ])
    )
}

const sanitizeImages = images => {
  if (!Array.isArray(images)) return []
  return images
    .filter(image => typeof image?.url === "string")
    .map(image => ({ url: image.url }))
}

const sanitizeAvailability = availability => {
  const channels = availability?.channels
  if (!channels || typeof channels !== "object") return undefined

  const safeChannels = Object.entries(channels).reduce(
    (result, [channelId, channel]) => {
      if (channel && typeof channel === "object") {
        result[channelId] = compactObject([
          ["availableQuantity", channel.availableQuantity],
        ])
      }
      return result
    },
    {}
  )

  return { channels: safeChannels }
}

const sanitizeDiscountObject = discount => {
  if (!discount || typeof discount !== "object") return undefined

  const value = discount.value
  const safeValue =
    value && typeof value === "object"
      ? compactObject([
          ["type", value.type],
          ["permyriad", value.permyriad],
          [
            "money",
            Array.isArray(value.money)
              ? value.money.map(sanitizeMoney).filter(Boolean)
              : undefined,
          ],
        ])
      : undefined

  return compactObject([
    ["id", discount.id],
    ["code", discount.code],
    ["name", cloneAllowedValue(discount.name)],
    ["requiresDiscountCode", discount.requiresDiscountCode],
    [
      "cartDiscounts",
      Array.isArray(discount.cartDiscounts)
        ? discount.cartDiscounts.map(item => compactObject([["id", item?.id]]))
        : undefined,
    ],
    ["value", safeValue],
  ])
}

const sanitizeDiscountReference = discount => {
  if (!discount || typeof discount !== "object") return undefined
  return compactObject([
    ["id", discount.id],
    ["obj", sanitizeDiscountObject(discount.obj)],
  ])
}

const sanitizeDiscountedPrice = discountedPrice => {
  if (!discountedPrice || typeof discountedPrice !== "object") return undefined

  return compactObject([
    ["value", sanitizeMoney(discountedPrice.value)],
    [
      "includedDiscounts",
      Array.isArray(discountedPrice.includedDiscounts)
        ? discountedPrice.includedDiscounts.map(item =>
            compactObject([
              ["discountedAmount", sanitizeMoney(item?.discountedAmount)],
              ["discount", sanitizeDiscountReference(item?.discount)],
            ])
          )
        : undefined,
    ],
  ])
}

const sanitizePrice = price => {
  if (!price || typeof price !== "object") return undefined
  const discounted = price.discounted

  return compactObject([
    ["value", sanitizeMoney(price.value)],
    [
      "discounted",
      discounted && typeof discounted === "object"
        ? compactObject([
            ["value", sanitizeMoney(discounted.value)],
            ["discount", sanitizeDiscountReference(discounted.discount)],
          ])
        : undefined,
    ],
  ])
}

const sanitizeBundleParentProduct = product => {
  const current = product?.obj?.masterData?.current
  if (!current || typeof current !== "object") return undefined

  const masterVariant = current.masterVariant
  const safeMasterVariant =
    masterVariant && typeof masterVariant === "object"
      ? compactObject([
          ["images", sanitizeImages(masterVariant.images)],
          ["attributes", sanitizeAttributes(masterVariant.attributes)],
          ["availability", sanitizeAvailability(masterVariant.availability)],
        ])
      : undefined

  return {
    obj: {
      masterData: {
        current: compactObject([
          ["name", cloneAllowedValue(current.name)],
          ["description", cloneAllowedValue(current.description)],
          ["slug", cloneAllowedValue(current.slug)],
          ["masterVariant", safeMasterVariant],
        ]),
      },
    },
  }
}

const sanitizeLineItemCustom = custom => {
  const fields = custom?.fields
  if (!fields || typeof fields !== "object") return undefined

  const safeFields = compactObject([
    ["isBundleChildItem", fields.isBundleChildItem],
    ["isBundleParent", fields.isBundleParent],
    ["bundleSku", fields.bundleSku],
    ["parentBundleSku", fields.parentBundleSku],
    ["bundleParentId", fields.bundleParentId],
    [
      "bundleParentProduct",
      sanitizeBundleParentProduct(fields.bundleParentProduct),
    ],
    ["bundleItemPrice", sanitizeMoney(fields.bundleItemPrice)],
    ["bundleTotalPrice", sanitizeMoney(fields.bundleTotalPrice)],
    ["isBackOrderable", fields.isBackOrderable],
  ])

  return Object.keys(safeFields).length ? { fields: safeFields } : undefined
}

const sanitizeLineItem = item => {
  if (!item || typeof item !== "object") return null

  const variant = item.variant
  const safeVariant =
    variant && typeof variant === "object"
      ? compactObject([
          ["sku", variant.sku],
          ["images", sanitizeImages(variant.images)],
          ["attributes", sanitizeAttributes(variant.attributes)],
          ["availability", sanitizeAvailability(variant.availability)],
        ])
      : undefined

  return compactObject([
    ["id", item.id],
    ["name", cloneAllowedValue(item.name)],
    ["productSlug", cloneAllowedValue(item.productSlug)],
    ["quantity", item.quantity],
    ["addedAt", item.addedAt],
    ["price", sanitizePrice(item.price)],
    ["discountedPrice", sanitizeDiscountedPrice(item.discountedPrice)],
    ["totalPrice", sanitizeMoney(item.totalPrice)],
    ["variant", safeVariant],
    ["custom", sanitizeLineItemCustom(item.custom)],
  ])
}

const sanitizePromoCode = promo => {
  if (!promo || typeof promo !== "object") return null
  return compactObject([
    ["type", promo.type],
    ["code", promo.code],
    ["name", cloneAllowedValue(promo.name)],
    ["discountedAmount", sanitizeMoney(promo.discountedAmount)],
  ])
}

const sanitizeDiscountCode = item => {
  if (!item || typeof item !== "object") return null
  return compactObject([
    ["discountCode", sanitizeDiscountReference(item.discountCode)],
  ])
}

const getCustomerId = cart => {
  const customerId = cart?.customerId
  if (!customerId) return ""
  if (typeof customerId === "string") return customerId
  return customerId.id || ""
}

const getCartShopperKey = cart =>
  buildCartLandingShopperKey({
    isAuth: Boolean(getCustomerId(cart)),
    customerId: getCustomerId(cart),
    anonymousId: cart?.anonymousId || "",
  })

export const stripCartLandingPii = (cart = {}) => {
  if (!cart || typeof cart !== "object") return null

  return compactObject([
    ["id", cart.id],
    ["shippingTotal", sanitizeMoney(cart.shippingTotal)],
    [
      "lineItems",
      Array.isArray(cart.lineItems)
        ? cart.lineItems.map(sanitizeLineItem).filter(Boolean)
        : [],
    ],
    [
      "customLineItems",
      Array.isArray(cart.customLineItems)
        ? cart.customLineItems.map(sanitizeLineItem).filter(Boolean)
        : [],
    ],
    [
      "promoCode",
      Array.isArray(cart.promoCode)
        ? cart.promoCode.map(sanitizePromoCode).filter(Boolean)
        : [],
    ],
    [
      "discountCodes",
      Array.isArray(cart.discountCodes)
        ? cart.discountCodes.map(sanitizeDiscountCode).filter(Boolean)
        : [],
    ],
    ["subtotalWithoutDiscount", sanitizeMoney(cart.subtotalWithoutDiscount)],
    ["subtotalWithPromotion", sanitizeMoney(cart.subtotalWithPromotion)],
    ["totalPromo", sanitizeMoney(cart.totalPromo)],
    ["totalTax", sanitizeMoney(cart.totalTax)],
    ["estimatedTotal", sanitizeMoney(cart.estimatedTotal)],
  ])
}

const getStorage = () => {
  if (typeof window === "undefined") return null
  try {
    return window.localStorage
  } catch (err) {
    console.warn("Cart landing snapshot storage is unavailable:", err)
    return null
  }
}

const getCurrentShopperContext = storage => {
  if (!storage) return { shopperKey: "", sessionId: "" }

  try {
    const rawAuth = storage.getItem("okta-token-storage")
    if (rawAuth && rawAuth !== "{}") {
      const auth = JSON.parse(rawAuth)
      const accessToken = auth?.accessToken
      const claims = accessToken?.claims || auth?.user || {}
      const customerId = claims.commercetoolsID || claims.commercetoolsId || ""
      if (customerId) {
        const sessionId =
          (typeof accessToken === "string"
            ? accessToken
            : accessToken?.accessToken) ||
          auth?.access_token ||
          window.authAccessToken ||
          `auth:${customerId}`
        return {
          shopperKey: buildCartLandingShopperKey({
            isAuth: true,
            customerId,
          }),
          sessionId,
        }
      }
    }

    const anonymousId = storage.getItem("anonymousId") || ""
    return {
      shopperKey: buildCartLandingShopperKey({ anonymousId }),
      sessionId: storage.getItem("okta-anonymous-access-token") || anonymousId,
    }
  } catch (err) {
    console.warn("Failed to resolve cart landing snapshot shopper:", err)
    return { shopperKey: "", sessionId: "" }
  }
}

const cancelPendingWrite = () => {
  writeGeneration += 1
  if (!pendingWrite || typeof window === "undefined") {
    pendingWrite = null
    return
  }

  try {
    if (
      pendingWrite.type === "idle" &&
      typeof window.cancelIdleCallback === "function"
    ) {
      window.cancelIdleCallback(pendingWrite.id)
    } else {
      window.clearTimeout(pendingWrite.id)
    }
  } catch (err) {
    console.warn("Failed to cancel a pending cart landing snapshot:", err)
  }
  pendingWrite = null
}

const removeSnapshot = storage => {
  if (!storage) return
  try {
    storage.removeItem(STORAGE_KEY)
    storage.removeItem(VIEW_MODEL_STORAGE_KEY)
  } catch (err) {
    console.warn("Failed to clear cart landing snapshot:", err)
  }
}

export const clearCartLandingSnapshot = () => {
  cancelPendingWrite()
  removeSnapshot(getStorage())
  clearCartLandingViewModel()
}

export const invalidateCartLandingSnapshot = (sessionId = "") => {
  const storage = getStorage()
  const currentShopper = getCurrentShopperContext(storage)
  invalidatedSessionId =
    sessionId || currentShopper.sessionId || currentShopper.shopperKey
  cancelPendingWrite()
  removeSnapshot(storage)
  clearCartLandingViewModel()
}

export const saveCartLandingSnapshot = (cart, shopperKey = "") => {
  const storage = getStorage()
  if (!storage) return false
  if (
    !cart?.id ||
    !Array.isArray(cart.lineItems) ||
    cart.lineItems.length === 0
  ) {
    clearCartLandingSnapshot()
    return false
  }

  const cartShopperKey = getCartShopperKey(cart)
  const key = shopperKey || cartShopperKey
  const currentShopper = getCurrentShopperContext(storage)
  if (!key || key !== cartShopperKey || key !== currentShopper.shopperKey) {
    return false
  }

  if (
    invalidatedSessionId &&
    invalidatedSessionId === currentShopper.sessionId
  ) {
    return false
  }
  if (
    invalidatedSessionId &&
    invalidatedSessionId !== currentShopper.sessionId
  ) {
    invalidatedSessionId = ""
  }

  const safeCart = stripCartLandingPii(cart)
  const snapshot = {
    version: SNAPSHOT_VERSION,
    savedAt: Date.now(),
    shopperKey: key,
    cartId: safeCart.id,
    cart: safeCart,
  }

  try {
    const serializedSnapshot = JSON.stringify(snapshot)
    if (serializedSnapshot.length > MAX_SNAPSHOT_LENGTH) {
      removeSnapshot(storage)
      return false
    }
    storage.setItem(STORAGE_KEY, serializedSnapshot)
    return true
  } catch (err) {
    console.warn("Failed to save cart landing snapshot:", err)
    removeSnapshot(storage)
    return false
  }
}

export const scheduleCartLandingSnapshot = (cart, shopperKey = "") => {
  if (typeof window === "undefined") return false
  if (
    !cart?.id ||
    !Array.isArray(cart.lineItems) ||
    cart.lineItems.length === 0
  ) {
    clearCartLandingSnapshot()
    return false
  }

  cancelPendingWrite()
  const generation = writeGeneration
  const writeSnapshot = () => {
    if (generation !== writeGeneration) return
    pendingWrite = null
    saveCartLandingSnapshot(cart, shopperKey)
  }

  try {
    if (typeof window.requestIdleCallback === "function") {
      pendingWrite = {
        type: "idle",
        id: window.requestIdleCallback(writeSnapshot, {
          timeout: IDLE_WRITE_TIMEOUT_MS,
        }),
      }
    } else {
      pendingWrite = {
        type: "timeout",
        id: window.setTimeout(writeSnapshot, 0),
      }
    }
  } catch (err) {
    pendingWrite = null
    console.warn("Failed to schedule a cart landing snapshot:", err)
    return false
  }

  return true
}

export const readCartLandingSnapshot = ({
  isAuth = false,
  customerId = "",
  anonymousId = "",
} = {}) => {
  const storage = getStorage()
  if (!storage) return null

  try {
    const raw = storage.getItem(STORAGE_KEY)
    if (!raw) return null

    const snapshot = JSON.parse(raw)
    if (
      !snapshot ||
      snapshot.version !== SNAPSHOT_VERSION ||
      !snapshot.cart?.id ||
      snapshot.cartId !== snapshot.cart.id ||
      !Array.isArray(snapshot.cart.lineItems) ||
      snapshot.cart.lineItems.length === 0
    ) {
      clearCartLandingSnapshot()
      return null
    }

    const snapshotAge = Date.now() - snapshot.savedAt
    if (
      typeof snapshot.savedAt !== "number" ||
      snapshotAge < 0 ||
      snapshotAge > MAX_AGE_MS
    ) {
      clearCartLandingSnapshot()
      return null
    }

    const explicitKey = buildCartLandingShopperKey({
      isAuth,
      customerId,
      anonymousId,
    })
    const currentShopper = getCurrentShopperContext(storage)
    if (
      invalidatedSessionId &&
      invalidatedSessionId === currentShopper.sessionId
    ) {
      return null
    }
    if (
      explicitKey &&
      currentShopper.shopperKey &&
      explicitKey !== currentShopper.shopperKey
    ) {
      clearCartLandingSnapshot()
      return null
    }
    const expectedKey = explicitKey || currentShopper.shopperKey

    if (!expectedKey) return null
    if (snapshot.shopperKey !== expectedKey) {
      clearCartLandingSnapshot()
      return null
    }

    const safeCart = stripCartLandingPii(snapshot.cart)
    if (!safeCart?.id || safeCart.lineItems.length === 0) {
      clearCartLandingSnapshot()
      return null
    }

    return {
      ...snapshot,
      cart: safeCart,
    }
  } catch (err) {
    console.warn("Failed to read cart landing snapshot:", err)
    clearCartLandingSnapshot()
    return null
  }
}
